import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Network, ChevronDown, ChevronRight, Mail, Phone, Building,
  ZoomIn, ZoomOut, X, ExternalLink, Briefcase
} from 'lucide-react';

const roleColors = {
  school_admin: { bg: 'bg-teal-100', border: 'border-teal-200', text: 'text-teal-700', label: 'Admin', gradient: 'from-teal-500 to-emerald-500' },
  hr: { bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-700', label: 'HR', gradient: 'from-amber-400 to-orange-500' },
  teacher: { bg: 'bg-indigo-100', border: 'border-indigo-200', text: 'text-indigo-700', label: 'Teacher', gradient: 'from-indigo-500 to-purple-500' },
};

const titleOrder = [
  'Principal', 'Vice Principal', 'Head Master', 'Head of Department',
  'Senior Teacher', 'Teacher', 'Lab Assistant', 'Librarian',
  'Counselor', 'Administrative Staff'
];

function QuickCard({ person, photoUrl, onClose, onViewProfile }) {
  const rc = roleColors[person.role] || roleColors.teacher;
  const cardRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm" data-testid="quick-card-overlay">
      <div ref={cardRef} className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-80 relative animate-in fade-in zoom-in-95 duration-200" data-testid={`quick-card-${person.user_id}`}>
        <button onClick={onClose} className="absolute top-3 right-3 h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors" data-testid="quick-card-close">
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-center gap-4 mb-4">
          {photoUrl ? (
            <img src={photoUrl} alt={person.full_name} className="h-14 w-14 rounded-xl object-cover border border-slate-200" />
          ) : (
            <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${rc.gradient} flex items-center justify-center`}>
              <span className="text-lg font-bold text-white">{person.full_name?.charAt(0)}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-800 truncate">{person.full_name}</h3>
            <p className="text-xs text-slate-500">{person.title || person.role?.replace('_', ' ')}</p>
            <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${rc.bg} ${rc.text}`}>{rc.label}</span>
          </div>
        </div>

        <div className="space-y-2.5 mb-4">
          {person.department && (
            <div className="flex items-center gap-2.5 text-xs">
              <Building className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-600">{person.department}</span>
            </div>
          )}
          {person.email && (
            <div className="flex items-center gap-2.5 text-xs">
              <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-600 truncate">{person.email}</span>
            </div>
          )}
          {person.phone && (
            <div className="flex items-center gap-2.5 text-xs">
              <Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-600">{person.phone}</span>
            </div>
          )}
          {person.employee_id && (
            <div className="flex items-center gap-2.5 text-xs">
              <Briefcase className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-600">ID: {person.employee_id}</span>
            </div>
          )}
        </div>

        <Button className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-xl text-xs" onClick={() => onViewProfile(person.user_id)} data-testid="quick-card-view-profile">
          <ExternalLink className="h-3.5 w-3.5 mr-2" /> View Full Profile
        </Button>
      </div>
    </div>
  );
}

function OrgNode({ person, isRoot, expanded, toggleExpand, children, onClickPerson, photoUrls }) {
  const rc = roleColors[person.role] || roleColors.teacher;
  const hasChildren = children && children.length > 0;

  return (
    <div className="flex flex-col items-center" data-testid={`org-node-${person.user_id}`}>
      <div
        className={`relative bg-white rounded-xl border shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-teal-300 transition-all duration-200 min-w-[180px] max-w-[220px] ${isRoot ? 'ring-2 ring-teal-200 border-teal-200' : 'border-slate-200'}`}
        onClick={() => onClickPerson(person)}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {photoUrls[person.user_id] ? (
              <img src={photoUrls[person.user_id]} alt={person.full_name} className="h-10 w-10 rounded-xl object-cover border border-slate-200" />
            ) : (
              <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${rc.gradient} flex items-center justify-center`}>
                <span className="text-sm font-bold text-white">{person.full_name?.charAt(0)}</span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-800 truncate">{person.full_name}</p>
            <p className="text-[10px] text-slate-500 truncate">{person.title || person.role?.replace('_', ' ')}</p>
            {person.department && <p className="text-[10px] text-slate-400 truncate">{person.department}</p>}
          </div>
        </div>
        <span className={`absolute -top-1.5 -right-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md text-[8px] font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>{rc.label}</span>
        {hasChildren && (
          <button
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-teal-500 hover:border-teal-300 transition-colors z-10 shadow-sm"
            onClick={(e) => { e.stopPropagation(); toggleExpand(person.user_id); }}
            data-testid={`toggle-${person.user_id}`}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        )}
      </div>

      {hasChildren && expanded && (
        <div className="flex flex-col items-center mt-4">
          <div className="w-px h-6 bg-slate-200" />
          {children.length > 1 && (
            <div className="flex items-start">
              <div className="flex">
                {children.map((_, i) => (
                  <div key={i} className={`h-px bg-slate-200 ${i === 0 ? 'w-[90px]' : i === children.length - 1 ? 'w-[90px]' : 'w-[180px]'}`} />
                ))}
              </div>
            </div>
          )}
          <div className={`flex gap-6 ${children.length > 3 ? 'flex-wrap justify-center max-w-4xl' : ''}`}>
            {children.map(child => (
              <div key={child.user_id} className="flex flex-col items-center">
                <div className="w-px h-6 bg-slate-200" />
                {child.node}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrgChart() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [zoom, setZoom] = useState(1);
  const [photoUrls, setPhotoUrls] = useState({});
  const [selectedDept, setSelectedDept] = useState('');
  const [departments, setDepartments] = useState([]);
  const [quickCardPerson, setQuickCardPerson] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/employees');
        const emps = res.data.data.employees;
        setEmployees(emps);
        const depts = [...new Set(emps.map(e => e.department).filter(Boolean))];
        setDepartments(depts);
        const roots = emps.filter(e => e.role === 'school_admin' || e.title === 'Principal' || e.title === 'Head Master');
        const expanded = {};
        roots.forEach(r => expanded[r.user_id] = true);
        setExpandedNodes(expanded);
      } catch { toast.error('Failed to load org chart'); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    employees.forEach(async (u) => {
      if (u.photo_path && !photoUrls[u.user_id]) {
        try {
          const resp = await api.get(`/files/${u.photo_path}`, { responseType: 'blob' });
          setPhotoUrls(prev => ({ ...prev, [u.user_id]: URL.createObjectURL(resp.data) }));
        } catch {}
      }
    });
  }, [employees]);

  const toggleExpand = useCallback((id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const expandAll = () => { const all = {}; employees.forEach(e => all[e.user_id] = true); setExpandedNodes(all); };
  const collapseAll = () => setExpandedNodes({});
  const onClickPerson = (person) => setQuickCardPerson(person);

  const buildTree = () => {
    let filtered = employees;
    if (selectedDept) filtered = employees.filter(e => e.department === selectedDept || e.role === 'school_admin' || e.role === 'hr');
    const admins = filtered.filter(e => e.role === 'school_admin');
    const hrs = filtered.filter(e => e.role === 'hr');
    const teachers = filtered.filter(e => e.role === 'teacher');
    const deptGroups = {};
    teachers.forEach(t => { const dept = t.department || 'General'; if (!deptGroups[dept]) deptGroups[dept] = []; deptGroups[dept].push(t); });
    Object.keys(deptGroups).forEach(dept => {
      deptGroups[dept].sort((a, b) => {
        const ai = titleOrder.indexOf(a.title || 'Teacher');
        const bi = titleOrder.indexOf(b.title || 'Teacher');
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    });
    return { admins, hrs, deptGroups };
  };

  const { admins, hrs, deptGroups } = buildTree();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="org-chart-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-500/20">
            <Network className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Organization Chart</h1>
            <p className="text-sm text-slate-500">{employees.length} members</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50" onClick={expandAll} data-testid="expand-all-btn">Expand All</Button>
          <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50" onClick={collapseAll} data-testid="collapse-all-btn">Collapse</Button>
          <div className="flex items-center gap-1 ml-2 bg-white rounded-lg p-1 border border-slate-200 shadow-sm">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}><ZoomOut className="h-3.5 w-3.5" /></Button>
            <span className="text-xs text-slate-500 min-w-[35px] text-center font-medium">{Math.round(zoom * 100)}%</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700" onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}><ZoomIn className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant={!selectedDept ? 'default' : 'outline'} className={!selectedDept ? 'bg-teal-500 text-white rounded-lg shadow-sm' : 'border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50'} onClick={() => setSelectedDept('')}>All Departments</Button>
        {departments.map(d => (
          <Button key={d} size="sm" variant={selectedDept === d ? 'default' : 'outline'} className={selectedDept === d ? 'bg-teal-500 text-white rounded-lg shadow-sm' : 'border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50'} onClick={() => setSelectedDept(d)}>{d}</Button>
        ))}
      </div>

      <div className="glass-card p-8 overflow-x-auto" data-testid="org-tree">
        <div className="min-w-[600px] flex flex-col items-center" style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s' }}>
          {admins.map(admin => (
            <OrgNode key={admin.user_id} person={admin} isRoot expanded={expandedNodes[admin.user_id]} toggleExpand={toggleExpand} onClickPerson={onClickPerson} photoUrls={photoUrls}
              children={[
                ...hrs.map(hr => ({ user_id: hr.user_id, node: <OrgNode key={hr.user_id} person={hr} expanded={expandedNodes[hr.user_id]} toggleExpand={toggleExpand} onClickPerson={onClickPerson} photoUrls={photoUrls} children={[]} /> })),
                ...Object.entries(deptGroups).map(([dept, members]) => {
                  const head = members[0]; const rest = members.slice(1);
                  return { user_id: `dept-${dept}`, node: (
                    <div className="flex flex-col items-center">
                      <div className="mb-2 px-3 py-1 rounded-lg bg-slate-50 border border-slate-200">
                        <div className="flex items-center gap-1.5">
                          <Building className="h-3 w-3 text-slate-400" />
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{dept}</span>
                          <span className="text-[10px] text-slate-300">({members.length})</span>
                        </div>
                      </div>
                      <div className="w-px h-3 bg-slate-200" />
                      <OrgNode person={head} expanded={expandedNodes[head.user_id]} toggleExpand={toggleExpand} onClickPerson={onClickPerson} photoUrls={photoUrls}
                        children={rest.map(t => ({ user_id: t.user_id, node: <OrgNode person={t} expanded={false} toggleExpand={toggleExpand} onClickPerson={onClickPerson} photoUrls={photoUrls} children={[]} /> }))} />
                    </div>
                  )};
                })
              ]}
            />
          ))}
          {admins.length === 0 && (
            <div className="text-center py-12">
              <Network className="h-16 w-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">No organizational structure found.</p>
              <p className="text-xs text-slate-400 mt-1">Add employees with titles and departments to build the org chart.</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-xs text-slate-400">Legend:</span>
        {Object.entries(roleColors).map(([role, rc]) => (
          <div key={role} className="flex items-center gap-1.5">
            <div className={`h-2.5 w-2.5 rounded ${rc.bg}`} />
            <span className="text-xs text-slate-500">{rc.label}</span>
          </div>
        ))}
        <span className="text-xs text-slate-400 ml-2">Click any node to see quick info</span>
      </div>

      {quickCardPerson && (
        <QuickCard person={quickCardPerson} photoUrl={photoUrls[quickCardPerson.user_id]}
          onClose={() => setQuickCardPerson(null)}
          onViewProfile={(userId) => { setQuickCardPerson(null); navigate(`/dashboard/employee/${userId}`); }} />
      )}
    </div>
  );
}
